import { withConsentUrl } from "@/app/utils/consent-url"

const BASE_API_V2 = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/consent/v2`

export type ConsentStatus = "granted" | "refused" | "withdrawn"
export type ConsentChannel = "phone" | "email"
export type ConsentQuoteType = "loan" | "health" | "travel_insurance"
export type ConsentActionType = "grant" | "withdraw" | "refuse"

export interface IConsentStatement {
    status: ConsentStatus
    channel: ConsentChannel
    collected_at: string
    expires_at: string | null
}

export interface IConsentPartner {
    partner: string | null
    label: string | null
    orias: string | null
    phone: string | null
    current: IConsentStatement
    history: IConsentStatement[]
}

export interface IConsentProduct {
    quote_type: string
    requested_at: string | null
    partners: IConsentPartner[]
}

export interface IConsentSpace {
    email: string
    updated_at: string | null
    products: IConsentProduct[]
}

export interface IConsentAction {
    quote_type: string
    partner: string | null
    channel: ConsentChannel
    action: ConsentActionType
}

// the drip step the visitor clicked (URL ?fe=): stored with the consent rows to tie a change back to the email that caused it
function withFromEmail<T extends Record<string, unknown>>(payload: T, fromEmail: string | null): T {
    return fromEmail ? { ...payload, from_email: fromEmail } : payload
}

export async function getConsentSpace(token: string): Promise<IConsentSpace | null> {
    try {
        const response = await fetch(`${BASE_API_V2}/${token}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
        })
        if (!response.ok) return null
        return await response.json()
    } catch (error) {
        console.error("Error fetching consent space:", error)
        return null
    }
}

export async function postConsentActions(
    token: string,
    actions: IConsentAction[],
    fromEmail: string | null = null
): Promise<boolean> {
    try {
        const response = await fetch(`${BASE_API_V2}/${token}/actions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl(withFromEmail({ actions }, fromEmail))),
            cache: "no-store",
        })
        if (!response.ok) return false
        const result = await response.json().catch(() => null)
        return result?.success === true
    } catch (error) {
        console.error("Error posting consent actions:", error)
        return false
    }
}

export async function grantPhoneConsent(token: string, fromEmail: string | null = null): Promise<boolean> {
    try {
        const response = await fetch(`${BASE_API_V2}/${token}/grant-phone`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl(withFromEmail({}, fromEmail))),
            cache: "no-store",
        })
        if (!response.ok) return false
        const result = await response.json().catch(() => null)
        return result?.success === true
    } catch (error) {
        console.error("Error granting phone consent:", error)
        return false
    }
}

/** Choix publicitaire du bandeau : persisté avec le lead, jamais réaffiché côté client. */
export async function postAdsConsent(
    quoteToken: string,
    quoteType: ConsentQuoteType,
    status: "granted" | "refused"
): Promise<boolean> {
    try {
        const response = await fetch(`${BASE_API_V2}/ads`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl({ quote_token: quoteToken, quote_type: quoteType, status })),
            cache: "no-store",
        })
        if (!response.ok) return false
        const result = await response.json().catch(() => null)
        return result?.success === true
    } catch (error) {
        console.error("Error posting ads consent:", error)
        return false
    }
}
